import { Injectable, signal } from '@angular/core';

const CRM_USER_ROLES_KEY = 'crm.user-roles';

/**
 * The signed-in user's roles, as `{ role_id, role_name, system_role_key }[]` from the API.
 * A user can hold several, so every check here is "has any of".
 *
 * Roles are kept as normalised keys (lower-case, letters and digits only) of both the
 * `system_role_key` and the `role_name`, so "Protection Compliance", "ProtectionCompliance"
 * and "PROTECTION_COMPLIANCE" all match the same thing.
 */
@Injectable({ providedIn: 'root' })
export class CrmUserRolesService {
  /** Signal-backed for the same reason as the permission state: role-gated `computed()`s
   *  must re-evaluate when the roles land after the first render. */
  private readonly state = signal<readonly string[]>([]);

  constructor() {
    this.hydrate();
  }

  hydrate(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const parsed = JSON.parse(localStorage.getItem(CRM_USER_ROLES_KEY) ?? 'null');
      if (Array.isArray(parsed)) {
        this.state.set(parsed.filter((key): key is string => typeof key === 'string'));
      }
    } catch {
      localStorage.removeItem(CRM_USER_ROLES_KEY);
    }
  }

  /** Accepts the raw `roles` array from the API. Anything that isn't an array is ignored
   *  rather than treated as "no roles", so a response without the field can't wipe them. */
  setRoles(roles: unknown): void {
    if (!Array.isArray(roles)) {
      return;
    }

    const keys = new Set<string>();
    for (const role of roles) {
      if (!role || typeof role !== 'object') {
        continue;
      }

      const record = role as Record<string, unknown>;
      for (const value of [record['system_role_key'], record['role_name']]) {
        const key = normalizeRoleKey(value);
        if (key) {
          keys.add(key);
        }
      }
    }

    this.state.set([...keys]);

    if (typeof window !== 'undefined') {
      localStorage.setItem(CRM_USER_ROLES_KEY, JSON.stringify([...keys]));
    }
  }

  clear(): void {
    this.state.set([]);

    if (typeof window !== 'undefined') {
      localStorage.removeItem(CRM_USER_ROLES_KEY);
    }
  }

  /** True when the user holds at least one of the given roles. */
  hasAnyRole(roles: readonly string[]): boolean {
    const held = this.state();
    return roles.some((role) => held.includes(normalizeRoleKey(role)));
  }
}

function normalizeRoleKey(value: unknown): string {
  return typeof value === 'string'
    ? value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        // The API spells it "Adviser"; tolerate the other spelling.
        .replace(/advisor/g, 'adviser')
    : '';
}
